import { CollapsableDraggableContainer, Icon, IDraggableElement, NoResults, Switch } from '@aon/aon-tws-common-ui';
import { ErrorMessages } from '@aon/aon-tws-common-ui/build/lib/components/Common/MessageHelper';
import { DragEndEvent } from '@dnd-kit/core';
import React, { useState } from 'react';
import arrayMove from 'array-move';
import { useDispatch, useSelector } from 'react-redux';
import styled from 'styled-components';
import ContentConstants from '../../../../contentConstants';
import {
	ElementType,
	IPageFlowElementModel,
	ISurveyElement,
	ISurveyQuestionEditModel,
	SurveyPropertyUnderValidation,
} from '../../../../models';
import { IApplicationState } from '../../../../store';
import { ActionCreators as SurveyActionCreators } from '../../../../store/SurveyStore';
import { RandomizedItemGroupingHelper, ValidationCollectionHelper, ValidationResultHelper } from '../../../../utilities';
import { useModalState } from '../../../../utilities/hooks';
import { QuestionBranchingLogicModal } from '../../../branching/QuestionBranchingLogicModal';
import { PageBreakRow } from '../../../surveys/PageBreakRow';
import { DraggableDescriptionTextRow2 } from '../draggables/DraggableDescriptionTextRow2';
import { DraggableHeaderRow } from '../draggables/DraggableHeaderRow';
import { DraggablePageBreakRow2 } from '../draggables/DraggablePageBreakRow2';
import { DraggableSectionBreakRow2 } from '../draggables/DraggableSectionBreakRow2';
import { DraggableSectionHeaderRow2 } from '../draggables/DraggableSectionHeaderRow2';
import { FormattedDraggableTable } from '../FormattedDesignTable';
import { QuestionDesignRow } from '../QuestionDesignRow';

export type BranchingAndLayoutTableContainerProps = {
	questions: ISurveyQuestionEditModel[];
	expanded: boolean;
	isSorting: boolean;
	onSortingChange: (itemIds: string[], isFirstPageRandomized: boolean) => void;
	updateBeforeSortStart: () => void;
	onEditPageFlowElement: (element?: IPageFlowElementModel) => void;
};

const local = {
	RandomizedBorder: styled.div`
		border: 1px dashed #bbbbbb;
		border-radius: 2px;
		padding-left: 14px;
		padding-top: 15px;
		padding-bottom: 15px;
		margin-bottom: 15px;
	`,

	SwitchWrapper: styled.div.attrs<{}>({
		'data-testid': 'tws-randomize-switch-wrapper',
	})`
		text-align: right;
		display: flex;
		justify-content: right;
		align-items: center;
	`,

	RandomizeTextWrapper: styled.div.attrs<{}>({
		'data-testid': 'tws-randomize-text-wrapper',
	})`
		text-align: right;
		color: #999999;
	`,

	RandomizeControlContainer: styled.div.attrs<{}>({
		'data-testid': 'tws-randomize-control',
	})`
		margin-bottom: 35px;
	`,

	FormattedPageFlowElementRow: styled.div.attrs<{}>({
		className: 'tws-formatted-page-flow-element-row',
		'data-testid': 'tws-formatted-page-flow-element-row',
	})`
		margin-bottom: 20px;
		margin-top: 10px;
		margin-right: 0px;
	`,
};

const CollapsableDraggableContainerDiv = styled.div`
	.tws-drag-container-div {
		margin-top: 30px;
	}
`;

export const BranchingAndLayoutTableContainer = (props: BranchingAndLayoutTableContainerProps) => {
	const dispatch = useDispatch();
	const surveyEditModel = useSelector((state: IApplicationState) => state.survey.surveyEditModel);
	const validationResults = useSelector((state: IApplicationState) => state.surveyBuilder.validationResults);
	const { isOpen: isQuestionBranchingLogicModalOpen, toggleModal: toggleQuestionBranchingLogicModal } = useModalState();
	const [displayLogicQuestionId, setDisplayLogicQuestionId] = useState(0);

	const validationCollectionHelper = new ValidationCollectionHelper<SurveyPropertyUnderValidation>(validationResults);
	const pageFlowElementResult = validationCollectionHelper.GetValidationResultForProperty(SurveyPropertyUnderValidation.pageFlowElements);
	const pageFlowValidationHelper = new ValidationResultHelper(pageFlowElementResult);

	const pageFlowElementsExist = surveyEditModel.pageFlowElements.length > 0;

	const totalPages = surveyEditModel.pageFlowElements.filter((e: ISurveyElement) => e.elementType === ElementType.PageBreak).length + 1;

	const surveyElements = ([...surveyEditModel.pageFlowElements] as ISurveyElement[]).concat([...props.questions]);
	const surveyElementSequences = surveyElements.filter((e) => e.elementType === ElementType.Question).map((e) => e.sequence ?? 0);
	const lastQuestionSequence = Math.max(...surveyElementSequences);
	const filteredsurveyElements = surveyElements.filter(
		(e) => e.elementType !== ElementType.Question || !(e as ISurveyQuestionEditModel).parentQuestionId,
	);

	let pageBreakCount = 1;

	filteredsurveyElements.sort((e1: ISurveyElement, e2: ISurveyElement) => (e1.sequence ?? 0) - (e2.sequence ?? 0));
	const randomizedGroups = RandomizedItemGroupingHelper.GroupSurveyElements(filteredsurveyElements, props.isSorting);

	const isPageOneRandomized = (surveyEditModel.questions.length > 0 && surveyEditModel.questions[0].isRandomized) ?? false;
	const isSomeItemRandomized = surveyEditModel.questions.reduce(
		(previousValue, currentValue) => previousValue || currentValue.isRandomized || false,
		false,
	);

	const questionsValidationResult = validationCollectionHelper.GetValidationResultForProperty(SurveyPropertyUnderValidation.questions);

	return (
		<>
			<FormattedDraggableTable>
				<local.FormattedPageFlowElementRow>
					<PageBreakRow pageNumber={1} totalPages={totalPages} propertyErrors={[]} propertyWarnings={[]} />
				</local.FormattedPageFlowElementRow>
				<local.RandomizeControlContainer>
					<local.SwitchWrapper>
						<b>
							<Switch
								text={ContentConstants.LOGIC_AND_LAYOUT.RANDOMIZE_SWITCH_TEXT}
								textwidth='200px'
								checked={isPageOneRandomized}
								onChange={(event: any) => dispatch(SurveyActionCreators.SetRandomizeItemsInSection(0, event.target.checked))}
							/>
						</b>
					</local.SwitchWrapper>
					{isPageOneRandomized && (
						<local.RandomizeTextWrapper>
							<Icon name='info' />
							&nbsp;{ContentConstants.RANDOMIZED_ITEMS_BOX_MESSAGE}
						</local.RandomizeTextWrapper>
					)}
				</local.RandomizeControlContainer>
				<DraggableHeaderRow
					pageFlowElementsExist={pageFlowElementsExist}
					expanded={props.expanded}
					randomized={isSomeItemRandomized}
					showLastHeader={true}
				/>
				{props.questions.length > 0 ? (
					<CollapsableDraggableContainerDiv>
						<CollapsableDraggableContainer
							sortedElements={filteredsurveyElements.map((e: ISurveyElement, index: number) => {
								if (e.elementType === ElementType.PageBreak) {
									pageBreakCount = pageBreakCount + 1;
								}
								return {
									id: e.id.toString(),
									keyIndex: e.sequence ?? 0,
									reactElement: renderRow(e, pageBreakCount),
									alterElementInDragging: renderRow(e, pageBreakCount),
									hideHandle: e.elementType === ElementType.Question,
									className: e.elementType === ElementType.Question ? '' : 'tws-drag-container-div',
									itemStyle: e.elementType === ElementType.Question ? { zIndex: 999 - index, position: 'relative' } : undefined,
								} as IDraggableElement;
							})}
							handleDragEnd={HandleDragEnd}
						/>
					</CollapsableDraggableContainerDiv>
				) : (
					<NoResults title='No Search Results' message='There are no results for this search' />
				)}
			</FormattedDraggableTable>
			{questionsValidationResult?.errors && questionsValidationResult?.errors?.length > 0 && (
				<ErrorMessages id={'error-messages-survey-questions'} messages={questionsValidationResult?.errors ?? []} />
			)}
			<QuestionBranchingLogicModal
				isOpen={isQuestionBranchingLogicModalOpen}
				onClosed={toggleQuestionBranchingLogicModal}
				questionId={displayLogicQuestionId}
			/>
		</>
	);

	function HandleDragEnd(event: DragEndEvent) {
		const { active, over } = event;
		if (over && active.id !== over.id) {
			const oldIndex = filteredsurveyElements.findIndex((x) => x.id.toString() === active.id);
			const newIndex = filteredsurveyElements.findIndex((x) => x.id.toString() === over.id);
			const sortedArray = arrayMove(filteredsurveyElements, oldIndex, newIndex);
			const sortedIds: string[] = [];
			sortedArray.forEach((item) => {
				sortedIds.push(item.id.toString());
			});
			props.onSortingChange(sortedIds, isPageOneRandomized || false);
		}
	}

	function renderRow(e: ISurveyElement, numPageBreaks: number) {
		const elementSequence = e.sequence ?? -1;
		const group = randomizedGroups.find((g) => elementSequence >= g.groupingIndexStart && elementSequence <= g.groupingIndexEnd);
		const isInRandomizedGroup = group != undefined;
		const isFirstInRandomizedGroup = group ? group.groupingIndexStart === elementSequence : false;
		const isLastInRandomizedGroup = group ? group.groupingIndexEnd === elementSequence : false;
		const randomizeGroupSize = group ? group.groupingIndexEnd - group.groupingIndexStart + 1 : 0;

		switch (e.elementType) {
			case ElementType.PageBreak:
				return (
					<DraggablePageBreakRow2
						key={e.id}
						index={e.sequence ?? 0}
						pageFlowValidationHelper={pageFlowValidationHelper}
						element={e as IPageFlowElementModel}
						onEdit={onEditPageFlowElement}
						onDelete={onDeleteSurveyElement}
						totalPages={totalPages}
						pageNumber={numPageBreaks}
						OnRandomizeChange={(sequenceStart: number, isSwitchOn: boolean) =>
							dispatch(SurveyActionCreators.SetRandomizeItemsInSection(sequenceStart, isSwitchOn))
						}
						canAccessRandomizedItems={true}
						pageFlowElementsExist={pageFlowElementsExist}
						expanded={props.expanded}
						randomized={isSomeItemRandomized}
					/>
				);
			case ElementType.SectionBreak:
				return (
					<DraggableSectionBreakRow2
						key={e.id}
						index={e.sequence ?? 0}
						pageFlowValidationHelper={pageFlowValidationHelper}
						element={e as IPageFlowElementModel}
						onEdit={onEditPageFlowElement}
						onDelete={onDeleteSurveyElement}
					/>
				);
			case ElementType.SectionHeader:
				return (
					<DraggableSectionHeaderRow2
						key={e.id}
						index={e.sequence ?? 0}
						pageFlowValidationHelper={pageFlowValidationHelper}
						element={e as IPageFlowElementModel}
						onEdit={onEditPageFlowElement}
						onDelete={onDeleteSurveyElement}
					/>
				);
			case ElementType.TextElement:
				return (
					<DraggableDescriptionTextRow2
						key={e.id}
						index={e.sequence ?? 0}
						pageFlowValidationHelper={pageFlowValidationHelper}
						element={e as IPageFlowElementModel}
						onEdit={onEditPageFlowElement}
						onDelete={onDeleteSurveyElement}
					/>
				);
			case ElementType.Question: {
				const question = e as ISurveyQuestionEditModel;
				return (
					!question.parentQuestionId && (
						<QuestionDesignRow
							key={e.id}
							index={e.sequence ?? 0}
							validationResults={validationResults}
							pageFlowWarnings={pageFlowValidationHelper.PropertyWarnings(e.surveyElementId)}
							ToggleDisplayLogicModal={(isOpen: boolean, surveyQuestionId: number) => {
								setDisplayLogicQuestionId(surveyQuestionId);
								toggleQuestionBranchingLogicModal();
							}}
							question={e as ISurveyQuestionEditModel}
							disableBranching={lastQuestionSequence === (e.sequence ?? 0) && (e as ISurveyQuestionEditModel).displayRulesCount < 1}
							expanded={props.expanded}
							searchText={''}
							pageFlowElementsExist={pageFlowElementsExist}
							isInRandomizedGroup={isInRandomizedGroup}
							isFirstInRandomizedGroup={isFirstInRandomizedGroup}
							isLastInRandomizedGroup={isLastInRandomizedGroup}
							randomizeGroupSize={randomizeGroupSize}
							isRandomizeOn={isSomeItemRandomized}
						/>
					)
				);
			}
		}
	}

	function onEditPageFlowElement(id: number) {
		const pageFlowElement = surveyEditModel.pageFlowElements.find((p) => {
			return p.id === id;
		});

		props.onEditPageFlowElement(pageFlowElement);
	}

	function onDeleteSurveyElement(id: number) {
		dispatch(SurveyActionCreators.DeleteSurveyElement(id));
	}
};
